import {ToastEnum, useErrorHandlerStore} from '~/modules/shared/composables/stores/error-handler';
import {parseISO} from 'date-fns';
import {useSessionStore} from '~/modules/shared/composables/stores/SessionStore';
import {DateTime} from 'luxon';
import {useUserStore} from '~/modules/shared/composables/stores/UserStore';
import type {TsammFilterOptions} from '~/modules/shared/composables/types/Common';
import {CorePermissionLevelEnum, PermissionEnum} from '~/modules/shared/composables/utils/Enums';
import {PermissionRulesEnum} from "~/modules/shared/composables/enums/permissions";

export const toastError = (title: string, message: string, timeout = 1000000) => {
  const useErrorHandler = useErrorHandlerStore();
  const fixedTimeout = 1000000; // this is to override any timeout passed in
  useErrorHandler.addToast({
    type: ToastEnum.error,
    title,
    message,
    timeout: fixedTimeout,
  });
};

export const toastSuccess = (title: string, message: string, timeout = 3000) => {
  const useErrorHandler = useErrorHandlerStore();
  const fixedTimeout = 3000; // this is to override any timeout passed in
  useErrorHandler.addToast({
    type: ToastEnum.success,
    title,
    message,
    timeout: fixedTimeout,
  });
};

export const toastWarning = (title: string, message: string, timeout = 1000000) => {
  const useErrorHandler = useErrorHandlerStore();
  const fixedTimeout = 1000000; // this is to override any timeout passed in
  useErrorHandler.addToast({
    type: ToastEnum.warning,
    title,
    message,
    timeout: fixedTimeout,
  });
};

export function exportUserSession(): { value: string | null } {
  return { value: localStorage.getItem('user-token') };
}

// export function destroyUserSession(): void {
//   localStorage.removeItem('user-token');
// }

export function setUserSession(session: string): void {
  localStorage.setItem('user-token', session);
}

export const globalFetchOptions = () => {
  const config = useRuntimeConfig();
  // check cookie.value if value does not exist, then take the value from localStorage
  const cookie = exportUserSession();
  return {
    baseURL: config.public.apiBase as string,
    headers: {
      Authorization: `Bearer ${cookie.value}`,
    },
  };
};

export const globalUseQueryOptions = () => {
  return {
    keepPreviousData: true,
    enabled: true,
    retry: 0,
    refetchOnWindowFocus: false,
  };
};

export const getTimeZone = (dateTimeString: string | undefined) => {
  if (dateTimeString === undefined) return '';

  const date = new Date(dateTimeString);

  const timezoneOffsetString = date.toLocaleString('en-US', { timeZoneName: 'short', hour12: false }).split(' ')[2];

  const timezoneAbbreviationMap: { [key: string]: string } = {
    'GMT-12': 'IDL',
    'GMT-11': 'NUT',
    'GMT-10': 'HST',
    'GMT-9': 'AKST',
    'GMT-8': 'PST',
    'GMT-7': 'MST',
    'GMT-6': 'CST',
    'GMT-5': 'EST',
    'GMT-4': 'AST',
    'GMT-3': 'BRT',
    'GMT-2': 'GST',
    'GMT-1': 'CET',
    'GMT+0': 'GMT',
    'GMT+1': 'CET',
    'GMT+2': 'EET',
    'GMT+3': 'MSK',
    'GMT+4': 'AMT',
    'GMT+5': 'PKT',
    'GMT+6': 'BST',
    'GMT+7': 'ICT',
    'GMT+8': 'CST',
    'GMT+9': 'JST',
    'GMT+10': 'AEST',
    'GMT+11': 'AEDT',
    'GMT+12': 'NZDT',
  };

  return timezoneAbbreviationMap[timezoneOffsetString] || timezoneOffsetString;
};

export const formatDate = (
  dateTimeString?: string | null,
  html = ' ',
  formatString = 'M/d/yyyy h:mm a ZZZZ',
  setZone = false
): string => {
  if (!dateTimeString) return '';
  dateTimeString = dateTimeString.replace('T00:00:00.000Z', '')
  if (dateTimeString.length === 10) {
    const [year, month, day] = dateTimeString.split('-');
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  }

  const dateTime = parseISO(dateTimeString);
  if (setZone) return DateTime.fromISO(dateTimeString).setZone('UTC').toFormat(formatString);
  else return DateTime.fromISO(dateTimeString).toFormat(formatString);
};

export function formatTime(
  timeString?: string | null,
  formatString: string = 'hh:mm a'
): string {
  if (!timeString) return '';

  const dt = DateTime.fromFormat(timeString, 'HH:mm:ss');

  if (!dt.isValid) {
    // Try parsing without seconds (HH:mm)
    const dtShort = DateTime.fromFormat(timeString, 'HH:mm');
    if (!dtShort.isValid) return '';
    return dtShort.toFormat(formatString);
  }

  return dt.toFormat(formatString);
}

export const addYears = (date: string, years: number) => {
  return DateTime.fromISO(date).plus({ years }).toString();
}

export const formatDate2 = (dateTimeString: string | undefined, html = ' ') => {
  if (dateTimeString === undefined) return '';
  const dateTime = new Date(dateTimeString);
  const month = dateTime.getMonth() + 1;
  const day = dateTime.getDate();
  const year = dateTime.getFullYear();
  let hours = dateTime.getHours();
  const minutes = dateTime.getMinutes();
  const amPm = hours >= 12 ? 'PM' : 'AM';
  hours %= 12;
  hours = hours || 12;
  return `${month}/${day}/${year}${html}${hours}:${minutes.toString().padStart(2, '0')} ${amPm}`;
};

export const formatToISOString = (
  dateTimeString?: string,
  html = ' ',
  formatString = 'M/d/yyyy h:mm a',
  setZone = false
): string => {
  if (!dateTimeString) return '';
  const [year, month, day, hour, minute, second] = dateTimeString.split(/\D+/).map(Number);

  // Create a UTC date object
  const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));

  // Get the timezone offset in minutes
  const timeZoneOffset = utcDate.getTimezoneOffset();

  // Create a new date by adding time adjusted for the timezone offset
  const adjustedDate = new Date(utcDate.getTime() + timeZoneOffset * 60000); // Multiply by 60000 to convert minutes to milliseconds

  return formatDate(adjustedDate.toISOString(), html, formatString, setZone);
};

export const generateFilter = (filterParam: TsammFilterOptions) => {
  if (filterParam === undefined) return '';
  let arFilter = '';
  Object.keys(filterParam).forEach(function (filter: string) {
    const filterItem = filterParam[filter][0];
    {
      // here filterItem.value can be of multiple types (string | number | boolean) type safety can't be used
      // eslint-disable-next-line eqeqeq
      if (filterItem.value != '') {
        let fvalue;
        if (Array.isArray(filterItem.value)) {
          fvalue = filterItem.value.join('|');
        } else {
          fvalue = filterItem.value;
        }

        if ((filter === 'createdAt' || filter === 'updatedAt') && filterItem.operator === 'IsLessThanOrEqualTo') {
          const date = new Date(fvalue as string);

          date.setDate(date.getDate() + 1);

          fvalue = date.toISOString().slice(0, 10);
        }

        if (filterParam[filter][1] !== undefined) {
          fvalue += '|' + filterParam[filter][1].value;
        }

        // don't want to pass in 'undefined' as a parameter
        if (typeof fvalue !== 'undefined' && fvalue !== '' && fvalue !== null && fvalue !== 'undefined') {
          arFilter += `${filter}=${fvalue}&`;
        }
      }
    }
  });
  console.log(arFilter);
  return arFilter;
};

/*export const generateFilterWithIndex = (filterParam: {
  [key: string]: { value: string | number | boolean; operator: string; valueLabel?: string[] }[];
}): {
  arFilter: string;
  index: number;
} => {
  if (filterParam === undefined)
    return {
      arFilter: '',
      index: 0,
    };
  let arFilter = '';
  let index = 0;
  Object.keys(filterParam).forEach(function (filter: string) {
    filterParam[filter].forEach(function (filterItem: {
      value: string | number | boolean;
      operator: string;
      valueLabel?: string[];
    }) {
      // here filterItem.value can be of multiple types (string | number | boolean) type safety cant be used
      // eslint-disable-next-line eqeqeq
      if (filterItem.value != '') {
        let fvalue;
        if (Array.isArray(filterItem.value)) {
          fvalue = filterItem.value.join(',');
        } else {
          fvalue = filterItem.value;
        }

        if ((filter === 'createdAt' || filter === 'updatedAt') && filterItem.operator === 'IsLessThanOrEqualTo') {
          const date = new Date(fvalue as string);

          date.setDate(date.getDate() + 1);

          fvalue = date.toISOString().slice(0, 10);
        }

        let optionsLabel = '';
        if (filterItem.valueLabel !== undefined) {
          optionsLabel = `&filters[${index}].ValueLabel=${filterItem.valueLabel.join(',')}`;
        }

        // don't want to pass in 'undefined' as a parameter
        if (typeof fvalue !== 'undefined' && fvalue !== '' && fvalue !== null && fvalue !== 'undefined') {
          arFilter += `filters[${index}].Field=${filter}&filters[${index}].Label=${getLabelFromField(
            filter
          )}&filters[${index}].Value=${fvalue}&filters[${index}].Operation=${filterItem.operator}${optionsLabel}&`;
        }

        index++;
      }
    });
  });
  return {
    arFilter,
    index,
  };
};*/

/*const getLabelFromField = (field: string) => {
  const labelFromField: { [key: string]: string } = {
    assignedTo: 'Assigned To',
    serviceAreaId: 'Service Area',
    status: 'Status',
    assistanceTypeId: 'Assistance Type',
    serviceAreaIds: 'Service Area',
    lastName: 'Last Name',
    firstName: 'First Name',
    payee: 'Payee',
    idPublic: 'ID',
    applicationIdPublic: 'applicationId',
    amountRequested: 'Amount Requested',
    amountApproved: 'Amount Approved',
    totalHouseholdMembers: 'Household Members',
    funds: 'Funds',
    fundingSources: 'Funding Sources',
    createdAt: 'Created Date',
    updatedAt: 'Updated Date',
    phoneNumber: 'Phone Number',
    hasOpenRequests: 'Has Open Requests',
    city: 'City',
    zipcode: 'Zipcode',
    state: 'State',
    emailAddress: 'Email Address',
    totalAssistanceRequests: 'Total AR',
    openAssistanceRequests: 'Open AR',
    name: 'Name',
    isActive: 'Active',
    currentBalance: 'Current Balance',
    balanceAwarded: 'Amount Awarded',
    balanceFunded: 'Issued Payments',
    pendingPayments: 'Pending Payments',
    balanceAvailable: 'Unspent Balance',
    zipCodes: 'ZipCode',
    fundingSourceID: 'Funding Source',
    active: 'Active',
    balanceAllocated: 'Current Allocation',
  };
  return labelFromField[field] ?? field;
};*/

/*export const generateColumns = (columnParam: { [key: string]: boolean }) => {
  const arColumns: string[] = [];
  let i = 0;
  Object.keys(columnParam).forEach(function (column: string) {
    if (columnParam[column]) {
      arColumns.push(`columns[${i}]=${column}`);
      i++;

      if (column === 'firstName') {
        arColumns.push(`columns[${i}]=lastName`);
        i++;
      } else if (column === 'address1') {
        arColumns.push(`columns[${i}]=address2`);
        i++;
        arColumns.push(`columns[${i}]=city`);
        i++;
        arColumns.push(`columns[${i}]=state`);
        i++;
        arColumns.push(`columns[${i}]=zipcode`);
        i++;
      }
    }
  });
  return arColumns.join('&');
};*/

export const generatePublicId = (id: number | undefined, prefix = 'CAR-') => {
  console.log(prefix);
  
  if (id === undefined) return '';
  if (prefix === 'DW') {
   return formatDisasterWorkerId(id);
  } else if (prefix === 'DOC') {
    return formatDocumentId(id);
  } else if (prefix === 'R') {
    return formatResourceId(id);
  } else if (prefix === 'CS') {
    return formatCoursesId(id);
  } else if (prefix === 'CL') {
    return formatClassesId(id);
  } else if (prefix === 'I') {
    return formatIncidentId(id);
  }

  return id
};

/*export const formatPhoneNumber = (input: string): string | null => {
  if (input === undefined) return '';
  // Remove any non-numeric characters from the input
  const cleanedInput = input.replace(/\D/g, '');

  // Check if the cleaned input has exactly 10 digits (standard for US phone numbers)
  if (cleanedInput.length !== 10) {
    return null; // or throw an error or handle it as you see fit
  }

  // Extract parts of the number
  const areaCode = cleanedInput.substring(0, 3);
  const centralOfficeCode = cleanedInput.substring(3, 6);
  const lineNumber = cleanedInput.substring(6, 10);

  // Format and return
  return `(${areaCode}) ${centralOfficeCode}-${lineNumber}`;
};*/

export const formatNumberToMoney = (amount: number, currencySign: 'standard' | 'accounting' | undefined = 'standard') => {
  if (amount === null || amount === undefined) return 0;

  return (amount / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    currencySign,
  });
};

export const isLoadingSpinner = (isLoading: boolean) => {
  const sessionStore = useSessionStore();
  sessionStore.setLoading(isLoading);
};

// expects a dateTimeString in the format of just YYYY-MM-DD and no time
/*export const formatDateForFilters = (dateTimeString: string | undefined) => {
  if (dateTimeString === undefined) return '';
  const parts = dateTimeString.split('-');
  if (parts.length < 3) return '';
  const year = parts[0];
  const month = parts[1];
  const day = parts[2];

  return `${month}/${day}/${year}`;
};*/

/*export const stripHtmlTags = (str: string) => {
  if (str === null || str === '') {
    return false;
  } else {
    str = str.toString();
    return str.replace(/<[^>]*>/g, '');
  }
};*/

/*export const getUserLevel = () => {
  const userStore = useUserStore();
  const user = userStore.currentUser;

  return [
    { label: 'Non-HQ', value: 'Non-HQ', attrs: { disabled: user?.locationLevel === 'NONHQ' } },
    { label: 'DHQ', value: 'DHQ', attrs: { disabled: user?.locationLevel === 'DHQ' } },
    { label: 'THQ', value: 'THQ', attrs: { disabled: user?.locationLevel === 'THQ' } },
  ];
};*/

/*export const formatDateDateTime = (dateTimeString: string | undefined, html = ' ') => {
  if (dateTimeString === undefined) return '';
  const dateTime = new Date(dateTimeString);
  const month = dateTime.getMonth() + 1;
  const day = dateTime.getDate();
  const year = dateTime.getFullYear();
  let hours = dateTime.getHours();
  const minutes = dateTime.getMinutes();
  const amPm = hours >= 12? 'PM' : 'AM';
  hours %= 12;
  hours = hours || 12;
  return `${month}/${day}/${year}${html}${hours}:${minutes.toString().padStart(2, '0')} ${amPm}`;
};*/

/*export const generateEvalPublicId = (id: number, prefix = 'EVAL-') => {
  const paddedNumber = id.toString().padStart(8, '0');
  return prefix + paddedNumber;
};*/
export const nl2br = (str: string) => {
  if (typeof str === 'undefined' || str === null) {
    return '';
  }
  return (str + '').replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1<br>$2');
};

/*
export const toProperCase = (str: string): string => {
  return str.replace(/\w\S*!/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};*/

export const isCanManage = () => {
  const userStore = useUserStore();
  return (
    userStore.currentUser?.tsammPermission.includes(PermissionEnum.CanManage) ||
    userStore.currentUser?.tsammPermission.includes(PermissionEnum.GodPermission)
  );
};

function formatFileSize(bytes: number) {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export const formatCurrency = (value: number) => {
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};
export function formatDisasterWorkerId(id: number | undefined, width = 6): string {
  if (!Number.isFinite(id) || !Number.isInteger(id)) {
    return '';
  }
  if (id === undefined || id < 0) {
    return '';
  }
  const padded = String(id).padStart(width, '0');
  return `EDS-DW-${padded}`;
}
export function formatCoursesId(id: number | undefined, width = 6): string {
  if (!Number.isFinite(id) || !Number.isInteger(id)) {
    return '';
  }
  if (id === undefined || id < 0) {
    return '';
  }
  const padded = String(id).padStart(width, '0');
  return `EDS-CS-${padded}`;
}
export function formatClassesId(id: number | undefined, width = 6): string {
  if (!Number.isFinite(id) || !Number.isInteger(id)) {
    return '';
  }
  if (id === undefined || id < 0) {
    return '';
  }
  const padded = String(id).padStart(width, '0');
  return `EDS-CL-${padded}`;
}
export function formatDocumentId(id: number | undefined, width = 6): string {
  if (!Number.isFinite(id) || !Number.isInteger(id)) {
    return '';
  }
  console.log(id);
  if (id === undefined || id < 0) {
    return '';
  }
  const padded = String(id).padStart(width, '0');
  return `EDS-DOC-${padded}`;
}
export function formatCommandPost(id: number | undefined, width = 6): string {
  if (!Number.isFinite(id) || !Number.isInteger(id)) {
    return '';
  }
  console.log(id);
  if (id === undefined || id < 0) {
    return '';
  }
  const padded = String(id).padStart(width, '0');
  return `EDS-ICP-${padded}`;
}
export function formatResourceId(id: number | undefined, width = 6): string {
  if (!Number.isFinite(id) || !Number.isInteger(id)) {
    return '';
  }
  if (id === undefined || id < 0) {
    return '';
  }
  const padded = String(id).padStart(width, '0');
  return `EDS-R-${padded}`;
}
export const maskPhoneNumber = (phoneNumber: string | undefined) => {
  if (!phoneNumber) return '';
  return phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
}

export function formatIncidentId(id: number | undefined, width = 6): string {
  if (!Number.isFinite(id) || !Number.isInteger(id)) {
    return '';
  }
  if (id === undefined || id < 0) {
    return '';
  }
  const padded = String(id).padStart(width, '0');
  return `EDS-D-${padded}`;
}

export const formatDateOfBirth = (dateOfBirth: number | undefined) => {
  if (!dateOfBirth) return '';
  const date = new Date(dateOfBirth);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

export const checkPermission = (permission: PermissionRulesEnum) => {
  const store = useUserStore();
  return store.currentUser.permissions.includes(permission);
};

export const checkPermissionLevel = (permissionLevel: CorePermissionLevelEnum) => {
  const store = useUserStore();
  return store.currentUser.permissionLevelNumber >= permissionLevel;
};